import { POST_STATUS } from '../statusWords';

/**
 * The Approvals queue's order and Ask for changes' quick chips (design
 * review round 27, §5 and §6). Pure, so the rules are tested on their own.
 */

/** What the order and the chips read off an item. */
export interface OrderItem {
  key: string;
  tag: string;
  failed?: boolean;
  /** When it was proposed; the queue runs oldest first within a group. */
  at: number;
  /** Shown with the approvals but not one (a draft campaign starting soon): last in its group. */
  notApproval?: boolean;
}

export type QueueGroup = 'TIME-SENSITIVE' | 'SITE CHANGES' | 'TO PUBLISH' | 'LATER';

const TIME_SENSITIVE = new RegExp(`^(${POST_STATUS.missed}|Social posts?|Schedule)$`);
const SITE_CHANGES = /^(Site change|New site)$/;

/**
 * 0 failed posts, 1 the rest of TIME-SENSITIVE, 2 SITE CHANGES, 3 TO PUBLISH
 * (Publish, Reach campaign and every other kind, as in the prototype).
 */
export function queueRank(item: Pick<OrderItem, 'tag' | 'failed'>): number {
  if (item.failed) return 0;
  if (TIME_SENSITIVE.test(item.tag)) return 1;
  if (SITE_CHANGES.test(item.tag)) return 2;
  return 3;
}

export function queueGroup(item: Pick<OrderItem, 'tag' | 'failed'>, later = false): QueueGroup {
  if (later) return 'LATER';
  const rank = queueRank(item);
  return rank <= 1 ? 'TIME-SENSITIVE' : rank === 2 ? 'SITE CHANGES' : 'TO PUBLISH';
}

const byRankThenAge = (a: OrderItem, b: OrderItem) =>
  queueRank(a) - queueRank(b) ||
  Number(Boolean(a.notApproval)) - Number(Boolean(b.notApproval)) ||
  a.at - b.at;

/** The queue in order: by group, oldest first within each; items marked Later go last, in the same order. */
export function orderQueue<T extends OrderItem>(items: T[], later: Iterable<string> = []): T[] {
  const deferred = new Set(later);
  const now = items.filter((i) => !deferred.has(i.key)).sort(byRankThenAge);
  const after = items.filter((i) => deferred.has(i.key)).sort(byRankThenAge);
  return [...now, ...after];
}

/**
 * Each row's group header, or null where the row continues the group above:
 * "TIME-SENSITIVE · 3". Expects the rows in orderQueue's order.
 */
export function queueHeads(items: OrderItem[], later: Iterable<string> = []): (string | null)[] {
  const deferred = new Set(later);
  const groups = items.map((i) => queueGroup(i, deferred.has(i.key)));
  const counts = new Map<QueueGroup, number>();
  for (const g of groups) counts.set(g, (counts.get(g) || 0) + 1);
  return groups.map((g, k) => (k === 0 || groups[k - 1] !== g ? `${g} · ${counts.get(g)}` : null));
}

/** What Ask for changes' chips read off an item. */
export interface ChipItem {
  tag: string;
  failed?: boolean;
  /** An open check, with its fix: "Fix the checks" leads. */
  fix?: unknown;
  preview: { type: string };
}

const ARTICLE_CHIPS = ['Shorter', 'Stronger headline', 'Different image'];
const POST_CHIPS = ['Shorter', 'Different time', 'Different image'];
const SITE_CHIPS = ['Smaller change', 'Different spot on the page'];

/**
 * Up to 3 quick chips under Ask for changes' note. "Fix the checks" leads
 * while a check is open; then the kind's own. Pages and forms take the site's
 * chips, campaigns the posts'. Datasets, agent requests and access requests
 * have none of their own.
 */
export function askChips(item: ChipItem): string[] {
  const { tag, preview } = item;
  const kind =
    item.failed || /post/i.test(tag) || tag === 'Social campaign' || tag === 'Sharing rule'
      ? POST_CHIPS
      : SITE_CHANGES.test(tag) || preview.type === 'site'
        ? SITE_CHIPS
        : preview.type === 'article'
          ? ARTICLE_CHIPS
          : [];
  return [...(item.fix ? ['Fix the checks'] : []), ...kind].slice(0, 3);
}

/** A chip adds its words to the note as a sentence ("Shorter."), once. */
export function appendChip(note: string, label: string, max = Infinity): string {
  const said = `${label}.`;
  const cur = note.trim();
  if (` ${cur} `.includes(` ${said} `)) return note;
  return (cur ? `${cur} ${said}` : said).slice(0, max);
}
